import React from "react";
import { Tooltip } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Card, CardContent } from "../ui/card";
import { 
  Collapsible, 
  CollapsibleTrigger,  
  CollapsibleContent 
} from "../ui/collapsible";
import { cn } from "../../lib/utils";
import { SessionContext } from "./AgentContext";
import { Turn } from "../../model_gen/session";
import { AgentInternalRequestType } from "../../types/AgentInternalRequestType";

interface AgentTimelineCardProps {
  contexts: SessionContext[];
  turns?: Turn[];
}

const AgentTimelineCard: React.FC<AgentTimelineCardProps> = ({
  contexts,
  turns
}) => {
  const [timelineOpen, setTimelineOpen] = React.useState(true);
  const [zoom, setZoom] = React.useState<number>(1);
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [hoveredBar, setHoveredBar] = React.useState<{ idx: number; x: number; y: number } | null>(null);
  const lastMouseEvent = React.useRef<{ clientX: number; clientY: number } | null>(null);

  const zoomIn = () => setZoom((z) => Math.min(5, Number((z * 1.2).toFixed(2))));
  const zoomOut = () => setZoom((z) => Math.max(1, Number((z / 1.2).toFixed(2))));

  const truncateMiddle = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    const halfLen = Math.floor((maxLength - 3) / 2);
    return text.substring(0, halfLen) + "..." + text.substring(text.length - halfLen);
  };

  // Build ordered contexts with fallbacks for missing data
  const orderedContexts = React.useMemo(() => {
    if (!turns || turns.length === 0) {
      return contexts.filter(
        (c) => typeof c.context_data?.start_time === "number" && typeof c.context_data?.end_time === "number"
      );
    }

    // Create a map of existing contexts by request_id
    const contextMap = new Map<string, SessionContext>();
    contexts.forEach(ctx => {
      if (ctx.request_id && typeof ctx.context_data?.start_time === "number" && typeof ctx.context_data?.end_time === "number") {
        contextMap.set(ctx.request_id, ctx);
      }
    });

    // Build ordered list based on turns
    const result: SessionContext[] = [];
    turns.forEach(turn => {
      // Handle routing results
      if ((turn as any).routing_result) {
        const routingResult = (turn as any).routing_result;
        const backendEpoch = routingResult.date_created;
        const timezoneOffsetMs = new Date().getTimezoneOffset() * 60 * 1000;
        const correctedEpoch = backendEpoch - timezoneOffsetMs;
        
        const startTime = correctedEpoch;
        const endTime = startTime + (routingResult.duration_ms || 0);
        
        const context: SessionContext = {
          agent_id: routingResult.agent_id || 0,
          request_id: `routing-${routingResult.id}`,
          operation_type: `${routingResult.routing_mode}_routing_${routingResult.evaluation_mode}`,
          request_operation: `${routingResult.routing_mode}_routing`,
          timestamp: startTime,
          context_data: {
            start_time: startTime,
            end_time: endTime,
            operation_type: `${routingResult.routing_mode}_routing`,
            agent_name: "Router"
          }
        };
        result.push(context);
        return;
      }
      
      // Handle regular action turns
      const requestId = turn.request?.request_id;
      if (!requestId) return;

      // Create context from database turn data (always use database timestamps)
      if (turn.response && typeof turn.response.response_time === "number" && typeof turn.request?.date_created === "number") {
        
          const backendEpoch = turn.request.date_created;
          const timezoneOffsetMs = new Date().getTimezoneOffset() * 60 * 1000;
          const correctedEpoch = backendEpoch - timezoneOffsetMs;
          
          const startTime = correctedEpoch;
          const endTime = startTime + turn.response.response_time;
        
        
        const context: SessionContext = {
          agent_id: 0, // Default value since we don't have this in turn data
          request_id: requestId,
          operation_type: turn.request.action_type || "unknown",
          request_operation: turn.request.action_type || "unknown",
          timestamp: startTime,
          context_data: {
            start_time: startTime,
            end_time: endTime,
            operation_type: turn.request.action_type || "unknown",
            agent_name: "Agent" // Required field
          }
        };
        result.push(context);
      }
    });

    return result;
  }, [contexts, turns]);

  // Get contexts with timing data for Gantt chart
  const ganttContexts = orderedContexts;

  const minStart = ganttContexts.length > 0
    ? Math.min(...ganttContexts.map((c) => c.context_data.start_time as number))
    : undefined;
  const maxEnd = ganttContexts.length > 0
    ? Math.max(...ganttContexts.map((c) => c.context_data.end_time as number))
    : undefined;

  const range = minStart !== undefined && maxEnd !== undefined ? Math.max(1, maxEnd - minStart) : 1;
  const laneHeight = 28;
  const totalDurationMs = ganttContexts.reduce((acc, c) => {
    const s = c.context_data.start_time as number;
    const e = c.context_data.end_time as number;
    return acc + Math.max(0, e - s);
  }, 0);

  const getBarClasses = (type: string) => {
    const normalizedType = type?.toLowerCase();
    const internalTypes = Object.values(AgentInternalRequestType).map(t => t.toLowerCase());
    
    // Routing operations get purple color
    if (normalizedType.includes("routing")) {
      return "bg-purple-500/50 border border-purple-600";
    }
    
    if (internalTypes.includes(normalizedType)) {
      return "bg-gray-500/50 border border-gray-600"; // Internal types are gray
    }
    
    // Non-internal types are blue
    return "bg-blue-500/50 border border-blue-600";
  };

  // Update tooltip position on scroll
  React.useEffect(() => {
    const handleScroll = () => {
      if (hoveredBar && lastMouseEvent.current && chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        const scrollLeft = chartRef.current.scrollLeft;
        setHoveredBar(prev => prev ? {
          ...prev,
          x: lastMouseEvent.current!.clientX - rect.left + scrollLeft,
          y: lastMouseEvent.current!.clientY - rect.top
        } : null);
      }
    };

    const container = chartRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [hoveredBar]);


  return (
    <Card className="border-border">
      <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
        <CardContent className={cn("px-4", timelineOpen ? "py-4" : "py-3")}>
          <CollapsibleTrigger className={cn("hover:opacity-80 transition-opacity", timelineOpen && "mb-3")}>
            <div className="text-xs font-medium text-muted-foreground uppercase">Agent Timeline</div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {minStart !== undefined && maxEnd !== undefined && (
              <div className="relative rounded-md bg-card px-3 py-4 overflow-hidden" style={{ height: `${ganttContexts.length * laneHeight + 24}px` }}>
                {/* Labels column */}
                <div className="absolute left-0 top-0 bottom-0 w-24 pr-2 pl-2">
                  {ganttContexts.map((c, idx) => (
                    <div key={idx} className="absolute left-0 right-0 h-6 leading-6 text-[11px] text-foreground/70 dark:text-foreground/90 whitespace-nowrap truncate" style={{ top: idx * laneHeight + 5 }}>
                      <Tooltip 
                        title={c.operation_type?.toUpperCase() || "UNKNOWN"} 
                        placement="topLeft"
                        getPopupContainer={() => document.body}
                        overlayStyle={{ zIndex: 9999 }}
                      >
                        <span>
                          {truncateMiddle(c.operation_type?.toUpperCase() || "UNKNOWN", 15)}
                        </span>
                      </Tooltip>
                    </div>
                  ))}
                  {/* Zoom controls at bottom of labels column */}
                  <div className="absolute left-0 right-0 bottom-0 flex items-center gap-1">
                    <button
                      aria-label="Zoom out"
                      onClick={zoomOut}
                      disabled={zoom <= 1}
                      className="h-5 w-5 inline-flex items-center justify-center rounded border border-border text-foreground/70 dark:text-foreground/90 hover:text-foreground hover:bg-accent/50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                      <MinusOutlined className="text-[9px]" />
                    </button>
                    <span className="text-[9px] font-mono text-foreground/70 dark:text-foreground/90 px-0.5">{zoom.toFixed(1)}x</span>
                    <button
                      aria-label="Zoom in"
                      onClick={zoomIn}
                      disabled={zoom >= 5}
                      className="h-5 w-5 inline-flex items-center justify-center rounded border border-border text-foreground/70 dark:text-foreground/90 hover:text-foreground hover:bg-accent/50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                      <PlusOutlined className="text-[9px]" />
                    </button>
                  </div>
                </div>
                {/* Chart area */}
                <div className="absolute left-24 right-20 top-0 bottom-0">
                  {/* Grid overlay tied to viewport (under bars) */}
                  <div className="absolute inset-0 z-0 pointer-events-none">
                    {/* Vertical axes at viewport edges */}
                    <div className="absolute left-0 top-0 bottom-5 w-px bg-border" />
                    <div className="absolute right-0 top-0 bottom-5 w-px bg-border" />
                    {/* Row baselines across viewport */}
                    {ganttContexts.map((_, idx) => (
                      <div key={`baseline-${idx}`} className="absolute left-0 right-0 h-px bg-border/70" style={{ top: idx * laneHeight + 7 }} />
                    ))}
                  </div>
                  <div ref={chartRef} className="relative z-10 h-full overflow-x-auto overflow-y-hidden">
                    <div className="relative" style={{ height: `${ganttContexts.length * laneHeight + 24}px`, width: `max(100%, ${100 * zoom}%)` }}>
                      {ganttContexts.map((c, idx) => {
                        const start = c.context_data.start_time as number;
                        const end = c.context_data.end_time as number;
                        const left = ((start - (minStart as number)) / range) * 100;
                        const width = Math.max(0.5, ((end - start) / range) * 100);
                        const top = idx * laneHeight + 4;
                        const timeTooltip = `${new Date(start).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})} - ${new Date(end).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})}`;
                        
                        return (
                          <div key={idx} className="absolute left-0" style={{ top, right: 0 }}>
                            <div
                              className={cn(
                                "absolute h-6 overflow-hidden z-10 cursor-pointer",
                                getBarClasses(c.operation_type)
                              )}
                              style={{ left: `${left * zoom}%`, width: `${width * zoom}%` }}
                              onMouseEnter={(e) => {
                                lastMouseEvent.current = { clientX: e.clientX, clientY: e.clientY };
                                const rect = chartRef.current?.getBoundingClientRect();
                                if (rect) {
                                  const scrollLeft = chartRef.current?.scrollLeft || 0;
                                  setHoveredBar({
                                    idx,
                                    x: e.clientX - rect.left + scrollLeft,
                                    y: e.clientY - rect.top
                                  });
                                }
                              }}
                              onMouseMove={(e) => {
                                lastMouseEvent.current = { clientX: e.clientX, clientY: e.clientY };
                                const rect = chartRef.current?.getBoundingClientRect();
                                if (rect) {
                                  const scrollLeft = chartRef.current?.scrollLeft || 0;
                                  setHoveredBar({
                                    idx,
                                    x: e.clientX - rect.left + scrollLeft,
                                    y: e.clientY - rect.top
                                  });
                                }
                              }}
                              onMouseLeave={() => {
                                lastMouseEvent.current = null;
                                setHoveredBar(null);
                              }}
                            >
                              {/* bar without inner label */}
                            </div>
                          </div>
                        );
                      })}
                      {/* Start time label */}
                      <span className="absolute left-2 bottom-0 text-[10px] font-mono text-foreground/70 dark:text-foreground/90 z-20 pointer-events-none">
                        {new Date(minStart).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})}
                      </span>
                      {/* End time label - positioned based on zoom */}
                      <span className="absolute bottom-0 text-[10px] font-mono text-foreground/70 dark:text-foreground/90 z-20 pointer-events-none" style={{ right: `calc(100% - ${100 * zoom}% + ${zoom === 1 ? 0 : 8}px)` }}>
                        {new Date(maxEnd).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})}
                      </span>
                      
                    </div>
                  </div>
                  {/* Custom tooltip - moved outside scrollable container */}
                  {hoveredBar && ganttContexts[hoveredBar.idx] && (
                    <div
                      className="absolute z-[10000] pointer-events-none"
                      style={{
                        left: `${hoveredBar.x - (chartRef.current?.scrollLeft || 0)}px`,
                        top: hoveredBar.idx === 0 ? `${hoveredBar.y + 10}px` : `${hoveredBar.y - 35}px`,
                        transform: "translateX(-50%)"
                      }}
                    >
                      <div className="bg-popover text-popover-foreground px-2 py-1 rounded shadow-lg border border-border text-xs whitespace-nowrap">
                        {`${new Date(ganttContexts[hoveredBar.idx].context_data.start_time).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})} - ${new Date(ganttContexts[hoveredBar.idx].context_data.end_time).toLocaleTimeString([], {hour12: true, hour: "numeric", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3})}`}
                      </div>
                    </div>
                  )}
                </div>
                {/* Right durations column */}
                <div className="absolute right-0 top-0 bottom-0 w-20 pl-2 pr-2">
                  {ganttContexts.map((c, idx) => {
                    const durationMs = Math.max(0, (c.context_data.end_time as number) - (c.context_data.start_time as number));
                    return (
                      <div key={idx} className="absolute right-0 h-6 leading-6 text-[11px] font-mono text-foreground/70 dark:text-foreground/90 text-right" style={{ top: idx * laneHeight + 5 }}>
                        {durationMs}ms
                      </div>
                    );
                  })}
                  <div className="absolute right-0 bottom-0 text-[10px] font-mono text-foreground/70 dark:text-foreground/90 text-right">
                    {totalDurationMs}ms
                  </div>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  );
};

export default AgentTimelineCard;
